import React, { useState, useEffect } from 'react';
import { 
    Plus, Edit, Trash2, Send, Calendar, Check, X, Eye, Mail, Tag, FileText
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getTags } from '../../services/tagService';
import { getCampaigns, createCampaign, updateCampaign, deleteCampaign, sendCampaign, getRecipients } from '../../services/mailingService';

// Email Templates
const EMAIL_TEMPLATES = {
    // 1. CONCEPTO: EDITORIAL (Autoridad, Seriedad, Lectura)
    // Ideal para: Gerentes que valoran el contenido y la reflexión estratégica.
    editorial: {
        name: 'Editorial',
        html: `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: 'Georgia', serif; background-color: #f8f8f8;">
    <table width="100%" border="0" cellspacing="0" cellpadding="0">
        <tr>
            <td align="center" style="padding: 40px 10px;">
                <table width="600" border="0" cellspacing="0" cellpadding="0" style="background: white; border-top: 5px solid #000; padding: 40px;">
                    <tr>
                        <td>
                            <p style="font-family: 'Arial', sans-serif; font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: #888; margin-bottom: 20px;">Management Report 2025</p>
                            <h1 style="font-size: 34px; line-height: 1.2; color: #111; margin-bottom: 30px;">La ineficiencia silenciosa que frena tu crecimiento.</h1>
                            <p style="font-size: 18px; line-height: 1.8; color: #333; margin-bottom: 20px;">
                                [NOMBRE_CLIENTE],
                            </p>
                            <p style="font-size: 18px; line-height: 1.8; color: #333; margin-bottom: 20px;">
                                Existe un punto de inflexión en toda empresa donde las herramientas que te trajeron hasta aquí, se convierten en el obstáculo para llegar allá.
                            </p>
                            <p style="font-size: 18px; line-height: 1.8; color: #333; margin-bottom: 30px;">
                                Hemos rediseñado nuestra plataforma no para "hacer más cosas", sino para eliminar el ruido operativo. Es una visión renovada de la gestión empresarial: más limpia, más rápida y dolorosamente honesta con los datos.
                            </p>
                            <a href="#" style="font-family: 'Arial', sans-serif; display: inline-block; border-bottom: 2px solid #000; color: #000; text-decoration: none; font-weight: bold; font-size: 14px; padding-bottom: 5px;">LEER EL REPORTE COMPLETO &rarr;</a>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    },

    // 2. CONCEPTO: SYSTEM UI (Tecnología, Notificación, Modernidad)
    // Ideal para: Mostrar que el software ha cambiado técnicamente. Vibe de "Silicon Valley".
    system: {
        name: 'Tech/SaaS',
        html: `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: 'Courier New', Courier, monospace; background-color: #0f172a;">
    <table width="100%" border="0" cellspacing="0" cellpadding="0">
        <tr>
            <td align="center" style="padding: 40px 10px;">
                <table width="500" border="0" cellspacing="0" cellpadding="0" style="background: #1e293b; border-radius: 8px; border: 1px solid #334155; color: #e2e8f0;">
                    <tr>
                        <td style="padding: 20px; border-bottom: 1px solid #334155;">
                            <span style="display: inline-block; width: 10px; height: 10px; background: #22c55e; border-radius: 50%; margin-right: 8px;"></span>
                            <span style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; font-weight: 600;">System Update v4.0.0 Live</span>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 30px;">
                            <p style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 16px; margin-bottom: 20px; color: #94a3b8;">
                                Hello [NOMBRE_CLIENTE]. El despliegue se ha completado.
                            </p>
                            <div style="background: #0f172a; padding: 15px; border-radius: 6px; margin-bottom: 20px; border: 1px dashed #475569;">
                                <p style="margin: 5px 0; font-size: 13px;">+ Core: Velocidad de procesamiento x3</p>
                                <p style="margin: 5px 0; font-size: 13px;">+ UI: Modo oscuro y navegación simplificada</p>
                                <p style="margin: 5px 0; font-size: 13px;">+ AI: Motor predictivo activado</p>
                            </div>
                            <p style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; margin-bottom: 25px; color: #cbd5e1;">
                                Tu cuenta antigua es elegible para una migración prioritaria con beneficios exclusivos.
                            </p>
                            <a href="#" style="font-family: 'Segoe UI', Arial, sans-serif; background: #3b82f6; color: white; text-decoration: none; padding: 10px 20px; border-radius: 4px; font-size: 14px; font-weight: 600; display: inline-block;">Iniciar sesión en v4.0</a>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    },

    // 3. CONCEPTO: THE CARD (Visual, Resumido, Estilo Red Social)
    // Ideal para: Dispositivos móviles. Mensaje rápido y visual.
    card: {
        name: 'Visual',
        html: `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Helvetica, Arial, sans-serif; background-color: #f1f5f9;">
    <table width="100%" border="0" cellspacing="0" cellpadding="0">
        <tr>
            <td align="center" style="padding: 40px 20px;">
                <table width="450" border="0" cellspacing="0" cellpadding="0" style="background: white; border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,0.08); overflow: hidden;">
                    <tr>
                        <td style="padding: 0;">
                            <img src="https://placehold.co/600x350/2563eb/ffffff?text=NUEVA+INTERFAZ&font=roboto" alt="New UI" style="width: 100%; display: block;">
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 30px;">
                            <h2 style="margin: 0 0 10px 0; font-size: 22px; color: #1e293b;">Menos clics. Más control.</h2>
                            <p style="margin: 0 0 20px 0; color: #64748b; line-height: 1.5;">
                                [NOMBRE_CLIENTE], hemos eliminado el 40% de los pasos innecesarios en tus procesos diarios.
                            </p>
                            <table width="100%" border="0">
                                <tr>
                                    <td>
                                        <a href="#" style="background: #1e293b; color: white; text-decoration: none; padding: 12px 0; display: block; text-align: center; border-radius: 8px; font-weight: 600;">Ver Demo de 1 min</a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    },

    // 4. CONCEPTO: COMPARISON (Split Screen, Antes vs Ahora, Racional)
    // Ideal para: Convencer lógicamente. Recuperar clientes perdidos mostrando mejora.
    comparison: {
        name: 'Split',
        html: `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: Helvetica, Arial, sans-serif; background-color: #ffffff;">
    <table width="100%" border="0" cellspacing="0" cellpadding="0">
        <tr>
            <td align="center" style="padding: 40px 10px;">
                <table width="600" border="0" cellspacing="0" cellpadding="0">
                    <tr>
                        <td style="text-align: center; padding-bottom: 30px;">
                            <h2 style="font-size: 24px; color: #333;">La evolución es evidente.</h2>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <table width="100%" border="0" cellspacing="0" cellpadding="0">
                                <tr>
                                    <td width="48%" style="background: #f5f5f5; padding: 20px; border-radius: 8px; vertical-align: top;">
                                        <p style="color: #999; font-size: 12px; font-weight: bold; text-transform: uppercase;">Antes (Manual)</p>
                                        <ul style="padding-left: 20px; color: #666; font-size: 14px; line-height: 1.6;">
                                            <li>Reportes en Excel</li>
                                            <li>Datos desactualizados</li>
                                            <li>Errores humanos</li>
                                        </ul>
                                    </td>
                                    <td width="4%"></td>
                                    <td width="48%" style="background: #eef2ff; border: 1px solid #c7d2fe; padding: 20px; border-radius: 8px; vertical-align: top;">
                                        <p style="color: #4f46e5; font-size: 12px; font-weight: bold; text-transform: uppercase;">Hoy (Nuestra Suite)</p>
                                        <ul style="padding-left: 20px; color: #333; font-size: 14px; line-height: 1.6;">
                                            <li><strong>Dashboards en Vivo</strong></li>
                                            <li><strong>IA Predictiva</strong></li>
                                            <li><strong>Automatización Total</strong></li>
                                        </ul>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td align="center" style="padding-top: 30px;">
                            <a href="#" style="background: #4f46e5; color: white; padding: 12px 30px; border-radius: 50px; text-decoration: none; font-weight: bold;">Actualizar mi negocio</a>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    },

    // 5. CONCEPTO: LETTER (Intimo, Texto Plano, CEO to Manager)
    // Ideal para: Recuperación de clientes "churneados". Se siente personal, no masivo.
    letter: {
        name: 'Personal Letter',
        html: `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; background-color: #ffffff;">
    <table width="100%" border="0" cellspacing="0" cellpadding="0">
        <tr>
            <td align="center" style="padding: 20px;">
                <table width="500" border="0" cellspacing="0" cellpadding="0" style="text-align: left;">
                    <tr>
                        <td style="padding: 20px 0;">
                            <p style="font-size: 15px; color: #333; line-height: 1.5;">Hola [NOMBRE_CLIENTE],</p>
                            <p style="font-size: 15px; color: #333; line-height: 1.5;">
                                Te escribo directamente porque estuve revisando nuestra lista de antiguos usuarios y noté que hace tiempo no colaboramos.
                            </p>
                            <p style="font-size: 15px; color: #333; line-height: 1.5;">
                                Siendo honestos, el software que usaste hace un año no estaba a la altura de lo que eres hoy. Lo sabíamos, y por eso pasamos los últimos 12 meses reconstruyendo el núcleo de nuestra tecnología.
                            </p>
                            <p style="font-size: 15px; color: #333; line-height: 1.5;">
                                No quiero venderte nada hoy. Solo quiero pedirte 10 minutos para mostrarte "La Nueva Versión". Creo genuinamente que resolverá el problema de gestión de inventarios que tenías.
                            </p>
                            <p style="font-size: 15px; color: #333; line-height: 1.5;">
                                ¿Te parece si te envío un video corto de cómo funciona ahora?
                            </p>
                            <br>
                            <p style="font-size: 15px; color: #333; line-height: 1.5;">Saludos,</p>
                            <p style="font-size: 15px; color: #000; font-weight: bold; line-height: 1.5;">El equipo de Producto</p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    },

    // 6. CONCEPTO: DATA DASHBOARD (Numérico, ROI, Prueba Social)
    // Ideal para: Gerentes Financieros o perfiles analíticos.
    data: {
        name: 'Data Driven',
        html: `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: 'Arial', sans-serif; background-color: #f4f4f4;">
    <table width="100%" border="0" cellspacing="0" cellpadding="0">
        <tr>
            <td align="center" style="padding: 40px 10px;">
                <table width="600" border="0" cellspacing="0" cellpadding="0" style="background: white; border-radius: 8px; overflow: hidden;">
                    <tr>
                        <td style="background: #2c3e50; padding: 40px; text-align: center;">
                            <h1 style="color: #27ae60; font-size: 48px; margin: 0; font-weight: 800;">+25%</h1>
                            <p style="color: white; font-size: 16px; margin: 10px 0 0 0; text-transform: uppercase; letter-spacing: 1px;">Incremento promedio en eficiencia</p>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 40px;">
                            <h2 style="color: #333; font-size: 20px; margin-top: 0;">Los números no mienten, [NOMBRE_CLIENTE].</h2>
                            <p style="color: #666; font-size: 16px; line-height: 1.6;">
                                Mientras lees esto, tus competidores están automatizando procesos que tú sigues haciendo manualmente. Nuestra nueva tecnología de gestión empresarial está diseñada para impactar directamente en el P&L (Ganancias y Pérdidas).
                            </p>
                            <table width="100%" cellpadding="10" style="margin-top: 20px; background: #f9f9f9; border-radius: 4px;">
                                <tr>
                                    <td style="border-bottom: 1px solid #eee;"><strong>Reducción de stock inactivo</strong></td>
                                    <td align="right" style="color: #27ae60;"><strong>-15%</strong></td>
                                </tr>
                                <tr>
                                    <td style="border-bottom: 1px solid #eee;"><strong>Velocidad de facturación</strong></td>
                                    <td align="right" style="color: #27ae60;"><strong>x2</strong></td>
                                </tr>
                            </table>
                            <div style="text-align: center; margin-top: 30px;">
                                <a href="#" style="background: #27ae60; color: white; padding: 15px 30px; text-decoration: none; border-radius: 4px; font-weight: bold;">Calcular mi ROI ahora</a>
                            </div>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    },

    // 7. CONCEPTO: FLOW (Proceso, Roadmap, Movimiento)
    // Ideal para: Mostrar que los llevas del punto A (Caos) al punto B (Orden).
    flow: {
        name: 'Roadmap',
        html: `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, sans-serif; background-color: #ffffff;">
    <table width="100%" border="0" cellspacing="0" cellpadding="0">
        <tr>
            <td align="center" style="padding: 40px 10px;">
                <table width="600" border="0" cellspacing="0" cellpadding="0">
                    <tr>
                        <td align="center">
                            <h1 style="color: #333; font-size: 26px;">Tu camino hacia la digitalización real</h1>
                            <p style="color: #666;">[NOMBRE_CLIENTE], simplificamos lo complejo.</p>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 40px 20px;">
                            <table width="100%" cellpadding="0" cellspacing="0">
                                <tr>
                                    <td width="50" align="center" valign="top">
                                        <div style="background: #e0e7ff; color: #4338ca; width: 30px; height: 30px; border-radius: 50%; text-align: center; line-height: 30px; font-weight: bold;">1</div>
                                        <div style="height: 40px; border-left: 2px dashed #e0e7ff; width: 0; margin-left: 14px;"></div>
                                    </td>
                                    <td style="padding-bottom: 30px;">
                                        <strong style="color: #333; font-size: 16px;">Diagnóstico IA</strong>
                                        <p style="margin: 5px 0 0 0; color: #777; font-size: 14px;">Nuestra herramienta escanea tus datos actuales.</p>
                                    </td>
                                </tr>
                                <tr>
                                    <td width="50" align="center" valign="top">
                                        <div style="background: #e0e7ff; color: #4338ca; width: 30px; height: 30px; border-radius: 50%; text-align: center; line-height: 30px; font-weight: bold;">2</div>
                                        <div style="height: 40px; border-left: 2px dashed #e0e7ff; width: 0; margin-left: 14px;"></div>
                                    </td>
                                    <td style="padding-bottom: 30px;">
                                        <strong style="color: #333; font-size: 16px;">Migración Automática</strong>
                                        <p style="margin: 5px 0 0 0; color: #777; font-size: 14px;">Traemos tu historial sin perder un solo byte.</p>
                                    </td>
                                </tr>
                                <tr>
                                    <td width="50" align="center" valign="top">
                                        <div style="background: #4338ca; color: #fff; width: 30px; height: 30px; border-radius: 50%; text-align: center; line-height: 30px; font-weight: bold;">3</div>
                                    </td>
                                    <td>
                                        <strong style="color: #333; font-size: 16px;">Gestión Optimizada</strong>
                                        <p style="margin: 5px 0 0 0; color: #777; font-size: 14px;">Tomas el control con nuestro nuevo dashboard.</p>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td align="center">
                            <a href="#" style="background: #4338ca; color: white; padding: 12px 25px; text-decoration: none; border-radius: 5px; font-weight: 600;">Comenzar el paso 1</a>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    },

    // 8. CONCEPTO: MINIMAL LUXURY (Exclusividad, Calidad Premium)
    // Ideal para: High-ticket sales o cuentas enterprise.
    minimal: {
        name: 'Minimalist',
        html: `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; font-family: 'Helvetica', Arial, sans-serif; background-color: #f7f7f7;">
    <table width="100%" border="0" cellspacing="0" cellpadding="0">
        <tr>
            <td align="center" style="padding: 60px 20px;">
                <table width="500" border="0" cellspacing="0" cellpadding="0" style="background: white; border: 1px solid #eee;">
                    <tr>
                        <td align="center" style="padding: 60px 40px;">
                            <img src="https://placehold.co/100x100/000000/ffffff?text=LOGO&font=roboto" alt="Logo" style="width: 50px; margin-bottom: 40px;">
                            
                            <p style="color: #000; font-size: 18px; font-weight: 300; margin-bottom: 30px; letter-spacing: 0.5px;">
                                Bienvenido a la nueva era de la gestión.
                            </p>
                            
                            <p style="color: #666; font-size: 14px; line-height: 1.8; margin-bottom: 40px; max-width: 400px;">
                                Hemos preparado un entorno dedicado para tu empresa. Descubre la potencia, la seguridad y el diseño que definen nuestra actualización 2025.
                            </p>

                            <a href="#" style="border: 1px solid #000; color: #000; padding: 15px 40px; text-decoration: none; text-transform: uppercase; font-size: 12px; letter-spacing: 2px;">Acceder</a>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`
    }
};

const MailingManager = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [formData, setFormData] = useState({
        Name: '',
        Subject: '',
        Body: '',
        TargetTags: [],
        ScheduledAt: null
    });
    const [previewMode, setPreviewMode] = useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [recipientsList, setRecipientsList] = useState([]);
    const [recipientsCount, setRecipientsCount] = useState(0);
    const [recipientsError, setRecipientsError] = useState(null);

    useEffect(() => {
        loadCampaigns();
        loadTags();
    }, []);

    const loadTags = async () => {
        try {
            const tags = await getTags();
            console.log('Loaded tags in MailingManager:', tags);
            setAvailableTags(tags);
        } catch (error) {
            console.error('Error loading tags:', error);
        }
    };

    const loadCampaigns = async () => {
        setLoading(true);
        try {
            const data = await getCampaigns();
            setCampaigns(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCampaign) {
                await updateCampaign(editingCampaign.CampaignId, formData);
            } else {
                await createCampaign(formData);
            }
            setIsModalOpen(false);
            setEditingCampaign(null);
            setFormData({ Name: '', Subject: '', Body: '', TargetTags: [], ScheduledAt: null });
            loadCampaigns();
        } catch (error) {
            alert('Error: ' + error.message);
        }
    };

    const handleEdit = (campaign) => {
        setEditingCampaign(campaign);
        setFormData({
            Name: campaign.Name,
            Subject: campaign.Subject,
            Body: campaign.Body,
            TargetTags: campaign.TargetTags || [],
            ScheduledAt: campaign.ScheduledAt ? new Date(campaign.ScheduledAt).toISOString().slice(0, 16) : null
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Eliminar esta campaña?')) {
            try {
                await deleteCampaign(id);
                loadCampaigns();
            } catch (error) {
                alert('Error: ' + error.message);
            }
        }
    };

    const handleSend = async (id) => {
        if (window.confirm('¿Enviar esta campaña ahora?')) {
            try {
                const result = await sendCampaign(id);
                alert(result.message);
                loadCampaigns();
            } catch (error) {
                alert('Error: ' + error.message);
            }
        }
    };

    const openNewModal = () => {
        setEditingCampaign(null);
        setFormData({ Name: '', Subject: '', Body: '', TargetTags: [], ScheduledAt: null });
        setIsModalOpen(true);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">Gestión de Mailing</h2>
                    <p className="text-slate-500">Configure y envíe campañas de correo a sus clientes.</p>
                </div>
                <button 
                    onClick={openNewModal}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm"
                >
                    <Plus size={20} /> Nueva Campaña
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-slate-400">Cargando campañas...</div>
            ) : (
                <div className="grid gap-4">
                    {campaigns.map(campaign => (
                        <div key={campaign.CampaignId} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h3 className="font-bold text-lg text-slate-800">{campaign.Name}</h3>
                                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                            campaign.Status === 'Sent' ? 'bg-emerald-100 text-emerald-700' :
                                            campaign.Status === 'Scheduled' ? 'bg-blue-100 text-blue-700' :
                                            'bg-slate-100 text-slate-600'
                                        }`}>
                                            {campaign.Status === 'Sent' ? 'Enviado' : campaign.Status === 'Scheduled' ? 'Programado' : 'Borrador'}
                                        </span>
                                    </div>
                                    <p className="text-slate-600 mb-2">Asunto: <span className="font-medium">{campaign.Subject}</span></p>
                                    <div className="flex items-center gap-4 text-sm text-slate-500">
                                        <span className="flex items-center gap-1">
                                            <Tag size={14} /> 
                                            {campaign.TargetTags && campaign.TargetTags.length > 0 
                                                ? campaign.TargetTags.join(', ') 
                                                : 'Todos los clientes'}
                                        </span>
                                        {campaign.SentAt && (
                                            <span className="flex items-center gap-1">
                                                <Send size={14} /> Enviado: {new Date(campaign.SentAt).toLocaleDateString()}
                                            </span>
                                        )}
                                        {campaign.ScheduledAt && !campaign.SentAt && (
                                            <span className="flex items-center gap-1">
                                                <Calendar size={14} /> Programado: {new Date(campaign.ScheduledAt).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={() => handleSend(campaign.CampaignId)}
                                        className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                        title={campaign.Status === 'Sent' ? 'Reenviar' : 'Enviar'}
                                    >
                                        <Send size={18} />
                                    </button>
                                    <button
                                        onClick={async () => {
                                            setRecipientsLoading(true);
                                            setRecipientsError(null);
                                            try {
                                                const data = await getRecipients(campaign.CampaignId);
                                                setRecipientsList(data.recipients || []);
                                                setRecipientsCount(data.count || (data.recipients || []).length);
                                                setRecipientsModalOpen(true);
                                            } catch (err) {
                                                setRecipientsError(err.message || 'Error al obtener destinatarios');
                                                setRecipientsList([]);
                                                setRecipientsCount(0);
                                                setRecipientsModalOpen(true);
                                            } finally {
                                                setRecipientsLoading(false);
                                            }
                                        }}
                                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Ver destinatarios"
                                    >
                                        <Mail size={18} />
                                    </button>
                                    <button 
                                        onClick={() => handleEdit(campaign)}
                                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Editar"
                                    >
                                        <Edit size={18} />
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(campaign.CampaignId)}
                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Eliminar"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {campaigns.length === 0 && (
                        <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                            No hay campañas creadas.
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-slate-800">
                                {editingCampaign ? 'Editar Campaña' : 'Nueva Campaña'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6">
                            <form id="campaignForm" onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Nombre de Campaña</label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            value={formData.Name}
                                            onChange={e => setFormData({...formData, Name: e.target.value})}
                                            placeholder="Ej: Newsletter Enero"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Asunto del Correo</label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            value={formData.Subject}
                                            onChange={e => setFormData({...formData, Subject: e.target.value})}
                                            placeholder="Ej: Novedades en e-ACM.rsk"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Etiquetas Objetivo</label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {availableTags.map(tag => {
                                            const isSelected = formData.TargetTags.includes(tag.Name);
                                            return (
                                                <button
                                                    key={tag.TagId}
                                                    type="button"
                                                    onClick={() => {
                                                        const currentTags = formData.TargetTags;
                                                        const newTags = isSelected 
                                                            ? currentTags.filter(t => t !== tag.Name)
                                                            : [...currentTags, tag.Name];
                                                        setFormData({...formData, TargetTags: newTags});
                                                    }}
                                                    className={`px-3 py-1 rounded-full text-sm font-medium border transition-all ${
                                                        isSelected 
                                                            ? 'bg-blue-100 text-blue-700 border-blue-200' 
                                                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                                                    }`}
                                                >
                                                    {tag.Name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {availableTags.length === 0 && (
                                        <p className="text-xs text-slate-400 italic">No hay etiquetas configuradas.</p>
                                    )}
                                    <p className="text-xs text-slate-500 mt-1">Solo se enviará a clientes que tengan al menos una de estas etiquetas. Por seguridad, si no selecciona ninguna etiqueta la campaña no será enviada.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Plantilla de Correo</label>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                        {Object.entries(EMAIL_TEMPLATES).map(([key, template]) => (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => setFormData({...formData, Body: template.html})}
                                                className="p-3 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FileText size={16} className="text-slate-500" />
                                                    <span className="text-sm font-medium text-slate-700">{template.name}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-xs text-slate-500">Selecciona una plantilla para comenzar. Puedes editar el HTML después de seleccionarla.</p>
                                </div>

                                <div className="flex-1 flex flex-col min-h-[400px]">
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-slate-700">Contenido HTML</label>
                                        <button 
                                            type="button"
                                            onClick={() => setPreviewMode(!previewMode)}
                                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                        >
                                            <Eye size={16} /> {previewMode ? 'Editar Código' : 'Ver Vista Previa'}
                                        </button>
                                    </div>
                                    
                                    {previewMode ? (
                                        <div 
                                            className="flex-1 border border-slate-300 rounded-lg p-4 overflow-y-auto bg-white prose max-w-none"
                                            dangerouslySetInnerHTML={{ __html: formData.Body }}
                                        />
                                    ) : (
                                        <textarea
                                            required
                                            className="flex-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                            value={formData.Body}
                                            onChange={e => setFormData({...formData, Body: e.target.value})}
                                            placeholder="<html><body><h1>Hola!</h1>...</body></html>"
                                        />
                                    )}
                                </div>
                            </form>
                        </div>

                        <div className="p-6 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                form="campaignForm"
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <Check size={18} /> Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Recipients Modal */}
            {recipientsModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-lg font-semibold">Destinatarios ({recipientsCount})</h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        // Export CSV
                                        const rows = recipientsList.map(r => ({ name: r.name, email: r.email }));
                                        const csv = [Object.keys(rows[0] || {}).join(','), ...rows.map(r => `${JSON.stringify(r.name).replace(/"/g,'')},${r.email}`)].join('\n');
                                        const blob = new Blob([csv], { type: 'text/csv' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url; a.download = `recipients_${Date.now()}.csv`; a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                    className="px-3 py-1 text-sm bg-slate-100 rounded hover:bg-slate-200"
                                >Exportar CSV</button>
                                <button onClick={() => setRecipientsModalOpen(false)} className="text-slate-500 hover:text-slate-700">
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="p-4 overflow-y-auto" style={{ maxHeight: '60vh' }}>
                            {recipientsLoading ? (
                                <div className="text-center py-8 text-slate-400">Cargando destinatarios...</div>
                            ) : recipientsError ? (
                                <div className="text-center py-8 text-red-500">{recipientsError}</div>
                            ) : recipientsList.length === 0 ? (
                                <div className="text-center py-8 text-slate-500">No hay destinatarios para esta segmentación.</div>
                            ) : (
                                <div className="space-y-2">
                                    {recipientsList.map(r => (
                                        <div key={r.ClientId} className="flex items-center justify-between p-2 rounded">
                                            <div>
                                                <div className="font-medium text-slate-800">{r.name}</div>
                                                <div className="text-sm text-slate-500">{r.email}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MailingManager;
